/**
 * @file    useChartTrend.ts
 * @scope   SHARED
 * @summary Owns the time-period ("trend") domain: the available trend options, the selected one,
 *          its interval value, and the mirroring of that selection into the global store.
 * @consumes Guardrails chart config payload, global store (`customWidget`, `setState`),
 *           Enterprise trend restorer (injected, see `restoreTrend`)
 * @produces chartTrend, selectedChartTrend + setter, parseTrendOptions, handleTrendChange,
 *           handleTrendCountChange, resetTrendToMinimum
 * @sideEffects Writes `enterpriseSelectedTrend` to the global store whenever the selection changes.
 * @notes Shared: legacy charts (VerticalBar, StackedBar, DataGrid rules) and Enterprise V2 both
 *        use trends. The Enterprise-specific restore step is injected as a callback so this module
 *        never imports from `components/DiyEnterprise/*`.
 */
import { useEffect, useRef, useState } from 'react';
import { TrendOptions } from 'core-ui/SelectTrendInput/SelectTrendInput';

/** Everything the drawer needs from the trend domain. */
export type UseChartTrendResult = {
  /** Trend options offered by Guardrails for the current chart. */
  chartTrend: TrendOptions[];
  /** Trend currently selected, `undefined` when the chart has no trend. */
  selectedChartTrend: TrendOptions | undefined;
  /** Raw setter, still needed by `SelectTrendInput` and the widget-rebuild flow. */
  setSelectedChartTrend: React.Dispatch<React.SetStateAction<TrendOptions | undefined>>;
  /** Replaces `chartTrend` with the options found in a Guardrails chart config. */
  parseTrendOptions: (chartData: any) => void;
  /** Clears the option list (charts that do not support trends). */
  clearTrendOptions: () => void;
  /** Handler for the trend dropdown: selecting a period resets its interval to the minimum. */
  handleTrendChange: (newValue: TrendOptions) => void;
  /** Handler for the interval input, clamped to the period's maximum. */
  handleTrendCountChange: (event: { target: { value: number } }) => void;
  /** Resets the current selection's interval to its minimum (used when the chart type changes). */
  resetTrendToMinimum: () => void;
};

/** Options accepted by {@link useChartTrend}. */
export type UseChartTrendParams = {
  /** Widget currently being edited (source of the persisted trend). */
  customWidget: any;
  /** Global store setter. */
  setState: (partial: any) => void;
  /** ENTERPRISE-ONLY: `true` while editing an Enterprise V2 widget. */
  isDiyWidgetEngine: boolean;
  /**
   * ENTERPRISE-ONLY: injected restorer that re-selects the persisted trend from a set of options.
   * Returns `undefined` when nothing has to be restored.
   */
  restoreTrend: (options: TrendOptions[], config: any) => TrendOptions | undefined;
};

export const useChartTrend = ({
  customWidget,
  setState,
  isDiyWidgetEngine,
  restoreTrend,
}: UseChartTrendParams): UseChartTrendResult => {
  const [chartTrend, setChartTrend] = useState<TrendOptions[]>([]);
  const [selectedChartTrend, setSelectedChartTrend] = useState<TrendOptions>();

  /**
   * Mirrors the selection into the store for the Enterprise preview to read.
   * WHY: the serialized comparison guards against the object identity changing on every render,
   * which would otherwise trigger an endless store update loop.
   */
  const prevTrendRef = useRef<string>('');
  useEffect(() => {
    const serialized = JSON.stringify(selectedChartTrend ?? null);
    if (serialized !== prevTrendRef.current) {
      prevTrendRef.current = serialized;
      setState({ enterpriseSelectedTrend: selectedChartTrend ?? null });
    }
  }, [selectedChartTrend]);

  /**
   * Builds the trend option list from a Guardrails chart config.
   *
   * WHY: when the chart exposes a single period and the widget has no persisted trend, that period
   * is auto-selected (with its minimum interval) so the preview is never left without a trend.
   */
  const parseTrendOptions = (chartData: any) => {
    const options: TrendOptions[] = [];

    if (chartData?.trend) {
      for (const t of chartData.trend) {
        options.push({ label: t.displayName, value: t.timePeriodType, trend: t });

        if (options?.length === 1 && !customWidget?.customWidgetConfig?.trend?.length) {
          setSelectedChartTrend(options[0]);
          const firstTrend = options[0].trend;
          if (firstTrend?.intervalMinValue !== undefined) {
            firstTrend.selectedValue = firstTrend.intervalMinValue;
          }
        }
      }

      // ENTERPRISE-ONLY: re-select the trend persisted in the enterprise config.
      if (isDiyWidgetEngine) {
        const restored = restoreTrend(options, customWidget.customWidgetConfig);
        if (restored) setSelectedChartTrend(restored);
      }
    }

    setChartTrend(options);
  };

  const clearTrendOptions = () => setChartTrend([]);

  /** Picking a new period always restarts its interval at the minimum allowed value. */
  const handleTrendChange = (newValue: TrendOptions) => {
    if (newValue?.trend) {
      setSelectedChartTrend({
        ...newValue,
        trend: { ...newValue.trend, selectedValue: newValue.trend?.intervalMinValue },
      });
    }
  };

  /** Clamps the typed interval to the period's maximum to avoid requesting an invalid range. */
  const handleTrendCountChange = (event: { target: { value: number } }) => {
    let val = Number(event.target.value);
    const maxValue = selectedChartTrend?.trend?.intervalMaxValue || 0;
    val = val > maxValue ? maxValue : val;

    if (event && selectedChartTrend) {
      setSelectedChartTrend({
        ...selectedChartTrend,
        trend: { ...selectedChartTrend?.trend, selectedValue: val },
      });
    }
  };

  /** Used when the chart type changes: the period survives, its interval restarts at the minimum. */
  const resetTrendToMinimum = () => {
    if (selectedChartTrend && selectedChartTrend?.trend?.selectedValue) {
      setSelectedChartTrend({
        ...selectedChartTrend,
        trend: {
          ...selectedChartTrend?.trend,
          selectedValue: selectedChartTrend.trend?.intervalMinValue,
        },
      });
    }
  };

  return {
    chartTrend,
    selectedChartTrend,
    setSelectedChartTrend,
    parseTrendOptions,
    clearTrendOptions,
    handleTrendChange,
    handleTrendCountChange,
    resetTrendToMinimum,
  };
};
